import { z } from 'zod'
import { HugeiconsIcon } from '@hugeicons/react'
import { DiamondIcon } from '@hugeicons/core-free-icons'

import { usePromoteUser } from '../services'
import type { AnyFieldApi } from '@tanstack/react-form'
import type { PromoteUserResponse } from '../types'
import { usePositionRoleLevels } from '@/features/direct-reports/hooks'
import { FieldInfo } from '@/components/common/field-info'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useFormDialog } from '@/contexts/form-dialog-context'
import { useConfirmDialog } from '@/contexts/confirm-dialog-context'

interface PromoteUserButtonProps {
  userId: string
  userName: string
  children?: React.ReactNode
  onSuccess?: (data: PromoteUserResponse) => void
  currentPosition?: {
    role: string
    level: string
    title: string
    sinceDate?: string
  } | null
}

interface PromoteUserFormData {
  positionRole: string
  positionLevel: string
  positionId: string
}

const PROMOTE_USER_SCHEMA = z.object({
  positionRole: z.string().min(1, 'Position role is required'),
  positionLevel: z.string().min(1, 'Position level is required'),
  positionId: z.string().min(1, 'Position ID is required'),
})

export const PromoteUserButton = ({
  userId,
  userName,
  children,
  onSuccess,
  currentPosition,
}: PromoteUserButtonProps) => {
  const { showFormDialog } = useFormDialog()
  const { showConfirmDialog } = useConfirmDialog()
  const promoteUserMutation = usePromoteUser()
  const { uniqueRoles, getLevelsForRole, findPositionId } =
    usePositionRoleLevels()

  const handleSubmit = async (data: PromoteUserFormData) => {
    // Show confirmation dialog before proceeding with promotion
    showConfirmDialog({
      title: 'Confirm Promotion',
      description: `Are you sure you want to promote ${userName} to ${data.positionLevel} - ${data.positionRole}?`,
      confirmText: 'Promote User',
      cancelText: 'Cancel',
      variant: 'default',
      onConfirm: async () => {
        const result = await promoteUserMutation.mutateAsync({
          userId,
          positionId: data.positionId,
        })
        onSuccess?.(result)
      },
    })

    // Return a resolved promise to satisfy the form dialog's type requirements
    return Promise.resolve({} as PromoteUserResponse)
  }

  const handlePromoteClick = () => {
    showFormDialog<PromoteUserFormData, PromoteUserResponse>({
      title: `Promotion of ${userName}`,
      description: `Select a new position role and level for ${userName}`,
      submitText: 'Promote User',
      showSuccessState: false,
      successTitle: 'User Promoted',
      successDescription: `${userName} has been successfully promoted to their new position!`,
      formOptions: {
        defaultValues: {
          positionRole: currentPosition?.role || '',
          positionLevel: currentPosition?.level || '',
          positionId: '',
        },
      },
      children: (form) => {
        return (
          <>
            {/* Current Position Info */}
            {currentPosition && (
              <div className="mb-4 p-3 bg-muted/30 rounded-lg space-y-1">
                <div className="text-sm font-medium">
                  Current Position: {currentPosition.title} (
                  {currentPosition.level})
                </div>
                {currentPosition.sinceDate && (
                  <div className="text-xs text-muted-foreground">
                    At position since{' '}
                    {new Date(currentPosition.sinceDate).toLocaleDateString(
                      'en-US',
                      {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                      },
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Position Role Field */}
            <form.Field
              name="positionRole"
              validators={{
                onDynamic: PROMOTE_USER_SCHEMA.shape.positionRole,
              }}
            >
              {(field: AnyFieldApi) => (
                <div className="space-y-2">
                  <Label htmlFor={field.name}>Position Role</Label>
                  <Select
                    value={field.state.value}
                    onValueChange={(value) => {
                      field.handleChange(value)
                      // Reset level when role changes
                      form.setFieldValue('positionLevel', '')
                      form.setFieldValue('positionId', '')
                    }}
                  >
                    <SelectTrigger id={field.name} className="w-full">
                      <SelectValue placeholder="Select a role" />
                    </SelectTrigger>
                    <SelectContent>
                      {uniqueRoles.map((role) => (
                        <SelectItem key={role} value={role}>
                          {role}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FieldInfo field={field} />
                </div>
              )}
            </form.Field>

            {/* Position Level Field */}
            <form.Subscribe
              selector={(state: { values: PromoteUserFormData }) =>
                state.values.positionRole
              }
            >
              {(selectedRole: string) => (
                <form.Field
                  name="positionLevel"
                  validators={{
                    onDynamic: PROMOTE_USER_SCHEMA.shape.positionLevel,
                  }}
                >
                  {(field: AnyFieldApi) => {
                    const levels = selectedRole
                      ? getLevelsForRole(selectedRole)
                      : []

                    return (
                      <div className="space-y-2">
                        <Label htmlFor={field.name}>Position Level</Label>
                        <Select
                          value={field.state.value}
                          onValueChange={(value) => {
                            field.handleChange(value)
                            // Derive positionId from role + level
                            if (selectedRole) {
                              const positionId = findPositionId(
                                selectedRole,
                                value,
                              )
                              if (positionId) {
                                form.setFieldValue('positionId', positionId)
                              }
                            }
                          }}
                          disabled={!selectedRole}
                        >
                          <SelectTrigger id={field.name} className="w-full">
                            <SelectValue placeholder="Select a level" />
                          </SelectTrigger>
                          <SelectContent>
                            {levels.map((levelOption) => {
                              // Disable levels that would be demotions (lower than current level)
                              const isDemotion =
                                currentPosition &&
                                selectedRole === currentPosition.role &&
                                levelOption.level.localeCompare(
                                  currentPosition.level,
                                ) < 0

                              return (
                                <SelectItem
                                  key={levelOption.id}
                                  value={levelOption.level}
                                  disabled={!!isDemotion}
                                >
                                  {levelOption.level} - {levelOption.title}
                                </SelectItem>
                              )
                            })}
                          </SelectContent>
                        </Select>
                        <FieldInfo field={field} />
                      </div>
                    )
                  }}
                </form.Field>
              )}
            </form.Subscribe>
          </>
        )
      },
      onSubmit: handleSubmit,
      onSuccess,
    })
  }

  return (
    <Button onClick={handlePromoteClick} variant="outline" size="sm">
      {children || (
        <>
          <HugeiconsIcon icon={DiamondIcon} className="h-4 w-4 mr-2" />
          Promote
        </>
      )}
    </Button>
  )
}
